/**
 * A function to create a log object for the 'templatesPageWebVitalsMetrics' event (evid: 100)
 *
 * Event description: Measures of page performance
 *
 * @param {templatesPageWebVitalsMetricsParams} params - The parameters for this event
 * @property {number} metric_value - The current value of the metric
 * @property {number} page_index - page_index
 * @property {string} id - A unique ID representing this particular metric that's specific to the current page. This ID can be used by an analytics tool to dedupe multiple values sent for the same metric, or to group multiple deltas together and calculate a total
 * @property {string} metric_name - The name of the metric (in acronym form: 'CLS' | 'FCP' | 'FID' | 'LCP' | 'TTFB')
 * @property {boolean} is_final - `false` if the value of the metric may change in the future, for the current page
 * @property {number} delta - The delta between the current value and the last-reported value. On the first report, `delta` and `value` will always be the same.
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<templatesPageWebVitalsMetricsParams>} LogObject<templatesPageWebVitalsMetricsParams>
 */
export function templatesPageWebVitalsMetrics(params) {
    return { evid: 100, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesStickyHeader' event (evid: 101)
 *
 * Event description: Fires every time the header changes to its sticky version or back to normal
 *
 * @param {templatesStickyHeaderParams} params - The parameters for this event
 * @property {string} sub_category - current  subcategory
 * @property {string} event_data - json with template list: returns all templates in the page loaded and their locations
 * @property {number} galleryDocsPerPage - number of templates in current page
 * @property {number} page_index - page_index
 * @property {string} header_type - header changed to sticky/default version
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} category - current category, "search" for search results
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<templatesStickyHeaderParams>} LogObject<templatesStickyHeaderParams>
 */
export function templatesStickyHeader(params) {
    return { evid: 101, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'clickAdiBannerInTemplatesPage' event (evid: 103)
 *
 * Event description: User clicks the banner in the templates page that leads to adi intro flow.
 *
 * @param {clickAdiBannerInTemplatesPageParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {string} banner_name - name of banner: adi, package picker, editor x, etc
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<clickAdiBannerInTemplatesPageParams>} LogObject<clickAdiBannerInTemplatesPageParams>
 */
export function clickAdiBannerInTemplatesPage(params) {
    return { evid: 103, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'sort' event (evid: 104)
 *
 * Event description: Sort.
 *
 * @param {sortParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {string} criteria - business term, if search
 * @property {string} subcategory - sub category slug
 * @property {string} actionName - expand or execute
 * @property {string} category - category slug
 * @property {string} values - Sorting category slug or "recommended" for default sorting.
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<sortParams>} LogObject<sortParams>
 */
export function sort(params) {
    return { evid: 104, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'clickSaveMobileTemplatesPage' event (evid: 105)
 *
 * Event description: user clicks save template in mobile flow
 *
 * @param {clickSaveMobileTemplatesPageParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {string} subcategory - template subcat
 * @property {GUID} metasiteid - metasite id
 * @property {string} category - template category
 * @property {GUID} esi - editor session id
 * @property {string} platform - platform
 * @property {string} imageUrl - image url
 * @property {number} page_number - page number of the template
 * @property {string} template_id - siteId
 * @property {string} product - product
 * @return {LogObject<clickSaveMobileTemplatesPageParams>} LogObject<clickSaveMobileTemplatesPageParams>
 */
export function clickSaveMobileTemplatesPage(params) {
    return { evid: 105, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'thankYouPageActionMobileTemplatesPage' event (evid: 106)
 *
 * Event description: User clicks one of the CTAs in the thank you page of the templates page mobile flow
 *
 * @param {thankYouPageActionMobileTemplatesPageParams} params - The parameters for this event
 * @property {string} action_name - "close" or "back-to-gallery"
 * @property {number} page_index - page_index
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<thankYouPageActionMobileTemplatesPageParams>} LogObject<thankYouPageActionMobileTemplatesPageParams>
 */
export function thankYouPageActionMobileTemplatesPage(params) {
    return { evid: 106, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'clickInstallAppInMobileTemplatePage' event (evid: 107)
 *
 * Event description: Users clicks to download wix app in the mobile templates page. For now there can be 2 entry points: mobile flow; email.
 *
 * @param {clickInstallAppInMobileTemplatePageParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {GUID} tpsi - tpsi
 * @property {string} entry_point - "mobile_flow" or "email"
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<clickInstallAppInMobileTemplatePageParams>} LogObject<clickInstallAppInMobileTemplatePageParams>
 */
export function clickInstallAppInMobileTemplatePage(params) {
    return { evid: 107, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesActionInViewerSideBar' event (evid: 108)
 *
 * Event description: This events is build to track all actions inside the viewer side bar. For now the only action name is "change color".
 *
 * @param {templatesActionInViewerSideBarParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {string} answerName - answer name
 * @property {GUID} tpsi - tpsi
 * @property {string} actionName - action name
 * @property {GUID} esi - editor session id
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<templatesActionInViewerSideBarParams>} LogObject<templatesActionInViewerSideBarParams>
 */
export function templatesActionInViewerSideBar(params) {
    return { evid: 108, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'websitePageTemplatesGeneralClick' event (evid: 109)
 *
 * Event description: Website Page Templates General Click
 *
 * @param {websitePageTemplatesGeneralClickParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {string} button_name - button_name
 * @property {string} platform - platform
 * @property {GUID} msid - msid
 * @property {string} product - product
 * @return {LogObject<websitePageTemplatesGeneralClickParams>} LogObject<websitePageTemplatesGeneralClickParams>
 */
export function websitePageTemplatesGeneralClick(params) {
    return { evid: 109, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'categoryInHp' event (evid: 110)
 *
 * Event description: Category in HP
 *
 * @param {categoryInHpParams} params - The parameters for this event
 * @property {string} structureId - structure_id
 * @property {string} location - Top, Bottom, Left etc' - Used in case of multiple buttons with the same name
 * @property {string} screen_version - screen version
 * @property {string} screenName - screen name
 * @property {number} page_index - page_index
 * @property {string} industryId - industry_id
 * @property {string} businessType - business_type
 * @property {string} segment - segment
 * @property {string} main_industry - main_industry
 * @property {string} subcat - business_term
 * @property {string} flow - flow
 * @property {string} subIndustry - sub_industry
 * @property {string} sourceScreen - new/vertical or new/intro or second/intro
 * @property {string} target_flow - The target flow for this user in the intro
 * @property {string} platform - platform
 * @property {string} cat - postreg
 * @property {string} action - action
 * @property {string} product - product
 * @return {LogObject<categoryInHpParams>} LogObject<categoryInHpParams>
 */
export function categoryInHp(params) {
    return { evid: 110, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesHoverOnTemplate' event (evid: 12)
 *
 * Event description: when user hover on a template
 *
 * @param {templatesHoverOnTemplateParams} params - The parameters for this event
 * @property {GUID} template_id - template id
 * @property {number} index - gallery doc index
 * @property {string} sub_category - List of subcategory filters
 * @property {number} galleryDocsPerPage - gallery doc page
 * @property {number} page_index - page_index
 * @property {string} book - book
 * @property {string} features - list of selected features
 * @property {boolean} videoThumbnail - template shows a video when hovering on the thumbnail (TRUE/FALSE)
 * @property {string} styles - list of selected styles
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {number} video_full_duration - the full duration of this video in seconds (not how long it played by the user)
 * @property {string} colors - list of selected colors
 * @property {number} hover_ms - hover duration in ms
 * @property {number} window_width - Initial width of client window
 * @property {string} sorting - selected sorting
 * @property {DateTime} video_end_time - thumbnail video end timestamp
 * @property {number} galleryDocIndex - gallery doc index
 * @property {string} origin - origin
 * @property {Url} openingbrowserurl - opening browse url
 * @property {number} siteType - site type
 * @property {string} screen_type - the type of page in which the action was performed: category / search_results / main / home
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - category
 * @property {string} video_status - 'have_nothing', 'have_metadata', 'have_current_data', 'have_future_data', 'have_enough_data'
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {DateTime} video_start_time - thumbnail video start timestamp
 * @property {number} video_playback_position - The position (timecode) in which the video was stopped, counting from the beginning or from the last looping of the video (in seconds)
 * @property {boolean} btn - is button
 * @property {GUID} openingSiteID - template id
 * @property {number} avail_height - Returns the amount of horizontal space in pixels vertical to the window
 * @property {string} product - product
 * @return {LogObject<templatesHoverOnTemplateParams>} LogObject<templatesHoverOnTemplateParams>
 */
export function templatesHoverOnTemplate(params) {
    return { evid: 12, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesViewTemplate' event (evid: 14)
 *
 * Event description: templates -> view template.
 *
 * @param {templatesViewTemplateParams} params - The parameters for this event
 * @property {number} index - gallerydocindex
 * @property {boolean} is_search_result - Is result from suggestion ("like you")
 * @property {number} search_rank - External ("like you") suggestion ranking
 * @property {string} sub_category - List of subcategory filters
 * @property {number} total_search_results - total search results from external ("like you") search suggestion
 * @property {boolean} is_recommended - is_recommended
 * @property {Url} openingBrowserUrl - openingbrowserurl
 * @property {string} score_vespa - vespa score
 * @property {number} page_index - page_index
 * @property {string} book - book
 * @property {string} features - list of selected features
 * @property {boolean} videoThumbnail - template shows a video when hovering on the thumbnail (TRUE/FALSE)
 * @property {string} button_name - button name
 * @property {number} gallerydocsperpage - gallerydocsperpage
 * @property {string} styles - list of selected styles
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {string} colors - list of selected colors
 * @property {GUID} search_guid - (optional) If from search results, GUID of matching search
 * @property {number} window_width - Initial width of client window
 * @property {string} sorting - selected sorting
 * @property {number} galleryDocIndex - gallerydocindex
 * @property {string} origin - origin
 * @property {number} siteType - site_type
 * @property {number} pageNumber - pagination
 * @property {string} screen_type - the type of page in which the action was performed: category / search_results / main / home
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - the create page category
 * @property {Url} http_referrer - http_referrer
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {boolean} btn - is_button
 * @property {GUID} openingSiteID - template_id
 * @property {number} avail_height - Returns the amount of vertical space in pixels available to the window
 * @property {string} product - product
 * @property {string} micro_category - micro_category
 * @property {string} previewType - new_tab / modal
 * @return {LogObject<templatesViewTemplateParams>} LogObject<templatesViewTemplateParams>
 */
export function templatesViewTemplate(params) {
    return { evid: 14, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesOpenEditor' event (evid: 15)
 *
 * Event description: Event fires every time that the user clicks edit in the templates page. disregard the entry point.
 *
 * @param {templatesOpenEditorParams} params - The parameters for this event
 * @property {GUID} template_id - template_id
 * @property {number} index - gallerydocindex
 * @property {boolean} is_search_result - Is result from suggestion ("like you")
 * @property {number} search_rank - External ("like you") suggestion ranking
 * @property {string} __cookies_prefix__wixsession - authentication token. if the session exists, then the user is logged in.
 * @property {string} sub_category - List of subcategory filters
 * @property {number} total_search_results - total search results from external ("like you") search suggestion
 * @property {boolean} is_recommended - is_recommended
 * @property {Url} openingBrowserUrl - openingbrowserurl
 * @property {string} template_type - ADI template or Editor template
 * @property {string} score_vespa - vespa score
 * @property {number} page_index - page_index
 * @property {Url} originappurl - only html template viewer send this property
 * @property {GUID} sub_category_id - Sub Category ID
 * @property {string} book - book
 * @property {string} features - list of selected features
 * @property {boolean} videoThumbnail - template shows a video when hovering on the thumbnail (TRUE/FALSE)
 * @property {number} gallerydocsperpage - gallerydocsperpage
 * @property {string} styles - list of selected styles
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {string} colors - list of selected colors
 * @property {GUID} search_guid - If from search results, GUID of matching search
 * @property {number} window_width - Initial width of client window
 * @property {string} sorting - selected sorting
 * @property {number} galleryDocIndex - gallerydocindex
 * @property {string} origin - origin  page of edit template click
 * @property {number} siteType - 1=flash, 2=html, 3=mobile, 4=facebook html, 5 = facebook flash, 6=blog(old)
 * @property {GUID} metasiteid - msid
 * @property {string} screen_type - the type of page in which the action was performed: category / search_results / main / home
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - the create page category
 * @property {GUID} category_id - Category ID
 * @property {Url} http_referrer - http_referrer
 * @property {number} window_height - Initial height of client window
 * @property {GUID} esi - Editor Session Id from templates page (new for every "view" and "edit" click))
 * @property {string} platform - platform
 * @property {boolean} btn - is_button
 * @property {GUID} openingSiteID - template_id
 * @property {number} avail_height - Returns the amount of vertical space in pixels available to the window
 * @property {number} page_number - pagination
 * @property {string} product - product
 * @property {string} micro_category - micro_category
 * @return {LogObject<templatesOpenEditorParams>} LogObject<templatesOpenEditorParams>
 */
export function templatesOpenEditor(params) {
    return { evid: 15, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'landingPageViewTemplate' event (evid: 16)
 *
 * Event description: landing page > view template.
 *
 * @param {landingPageViewTemplateParams} params - The parameters for this event
 * @property {number} page_index - the category index of the template
 * @property {string} landing_url - landing_url
 * @property {number} gallerydocsperpage - gallerydocsperpage
 * @property {number} galleryDocIndex - a zero-based index of the template on the page
 * @property {Url} openingbrowserurl - openingbrowserurl
 * @property {number} siteType - site_type
 * @property {string} platform - platform
 * @property {GUID} openingSiteID - template_id
 * @property {string} product - product
 * @return {LogObject<landingPageViewTemplateParams>} LogObject<landingPageViewTemplateParams>
 */
export function landingPageViewTemplate(params) {
    return { evid: 16, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'homePageStartNowButton' event (evid: 18)
 *
 * Event description: home page -> start now button.
 *
 * @param {homePageStartNowButtonParams} params - The parameters for this event
 * @property {string} dest - dest
 * @property {number} page_index - page_index
 * @property {string} origin - origin
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<homePageStartNowButtonParams>} LogObject<homePageStartNowButtonParams>
 */
export function homePageStartNowButton(params) {
    return { evid: 18, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesReadMore' event (evid: 19)
 *
 * Event description: templates -> read more.
 *
 * @param {templatesReadMoreParams} params - The parameters for this event
 * @property {Url} http_referrer - app_url
 * @property {number} page_index - the category index of the template
 * @property {Url} originappurl - only html template viewer send this property
 * @property {number} galleryDocIndex - gallerydocindex
 * @property {number} siteType - site_type
 * @property {string} category - the create page category
 * @property {string} platform - platform
 * @property {GUID} openingSiteID - template_id
 * @property {string} product - create page product filter
 * @return {LogObject<templatesReadMoreParams>} LogObject<templatesReadMoreParams>
 */
export function templatesReadMore(params) {
    return { evid: 19, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'homePageClickOnTemplate' event (evid: 21)
 *
 * Event description: home page -> click on template.
 *
 * @param {homePageClickOnTemplateParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {GUID} context - template_id
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<homePageClickOnTemplateParams>} LogObject<homePageClickOnTemplateParams>
 */
export function homePageClickOnTemplate(params) {
    return { evid: 21, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesStartedLoadingThePage' event (evid: 22)
 *
 * Event description: templates -> started loading the page.
 *
 * @param {templatesStartedLoadingThePageParams} params - The parameters for this event
 * @property {string} referralInfo - -
 * @property {string} interaction_type - Type of templates page interaction
 * @property {string} sub_category - List of subcategory filters
 * @property {string} event_data - data about templates loaded
 * @property {number} galleryDocsPerPage - gallerydocsperpage
 * @property {number} page_index - the category index of the template
 * @property {string} book - book
 * @property {string} features - list of selected features
 * @property {string} styles - list of selected styles
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {string} colors - list of selected colors
 * @property {string} sorting - selected sorting
 * @property {string} origin - origin page
 * @property {string} category - the create page category
 * @property {Url} http_referrer - http_referrer
 * @property {string} platform - platform
 * @property {string} product - create page product filter
 * @return {LogObject<templatesStartedLoadingThePageParams>} LogObject<templatesStartedLoadingThePageParams>
 */
export function templatesStartedLoadingThePage(params) {
    return { evid: 22, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesFinishedLoadingThePage' event (evid: 23)
 *
 * Event description: templates -> finished loading the page.
 *
 * @param {templatesFinishedLoadingThePageParams} params - The parameters for this event
 * @property {string} interaction_type - Type of templates page interaction
 * @property {string} sub_category - List of subcategory filters
 * @property {string} event_data - json with template list: returns all templates in the page loaded and their locations
 * @property {number} galleryDocsPerPage - gallerydocsperpage
 * @property {number} page_index - the category index of the template
 * @property {string} book - book
 * @property {string} features - list of selected features
 * @property {string} styles - list of selected styles
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {string} colors - list of selected colors
 * @property {string} sorting - selected sorting
 * @property {number} loadingTime - time to load the page
 * @property {string} origin - origin page
 * @property {string} category - the create page category
 * @property {Url} http_referrer - http_referrer
 * @property {string} platform - platform
 * @property {string} product - create page product filter
 * @return {LogObject<templatesFinishedLoadingThePageParams>} LogObject<templatesFinishedLoadingThePageParams>
 */
export function templatesFinishedLoadingThePage(params) {
    return { evid: 23, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesClickedDescription' event (evid: 24)
 *
 * Event description: templates -> clicked description.
 *
 * @param {templatesClickedDescriptionParams} params - The parameters for this event
 * @property {number} galleryDocsPerPage - gallery docs per page
 * @property {number} page_index - page_index
 * @property {GUID} tpsi - Templates Page Session ID
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<templatesClickedDescriptionParams>} LogObject<templatesClickedDescriptionParams>
 */
export function templatesClickedDescription(params) {
    return { evid: 24, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesClosedDescription' event (evid: 25)
 *
 * Event description: templates -> closed description.
 *
 * @param {templatesClosedDescriptionParams} params - The parameters for this event
 * @property {number} galleryDocsPerPage - gallery docs per page
 * @property {number} page_index - page_index
 * @property {GUID} tpsi - Templates Page Session ID
 * @property {string} closed_from - "x button"- user clicked on x, "hover out" - user hovered out  of description
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<templatesClosedDescriptionParams>} LogObject<templatesClosedDescriptionParams>
 */
export function templatesClosedDescription(params) {
    return { evid: 25, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesSearch' event (evid: 26)
 *
 * Event description: templates -> search.
 *
 * @param {templatesSearchParams} params - The parameters for this event
 * @property {number} totalPages - total_pages
 * @property {number} currentPage - current_page
 * @property {string} source - the source of the search query
 * @property {string} previous_sub_category - a sub category in which the search was performed
 * @property {string} click_source - Main Header/Sticky Header
 * @property {number} page_index - page_index
 * @property {string} criteria - criteria
 * @property {number} numberOfTemplatesInCurrentPage - templates_cnt
 * @property {string} book - book
 * @property {string} features - list of selected features
 * @property {string} selectedStructureId - structure_id of the selected term (if taken from ADI)
 * @property {string} searchType - search_type
 * @property {string} styles - list of selected styles
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {string} colors - list of selected colors
 * @property {GUID} search_guid - Unique GUID of search
 * @property {string} sorting - selected sorting
 * @property {string} category - category = search
 * @property {string} selectedIndustryId - industry_id of the selected term (if taken from ADI)
 * @property {string} platform - platform
 * @property {string} previous_category - category in which the search was performed
 * @property {string} product - product
 * @return {LogObject<templatesSearchParams>} LogObject<templatesSearchParams>
 */
export function templatesSearch(params) {
    return { evid: 26, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesSwitchToMobile' event (evid: 27)
 *
 * Event description: templates -> switch to mobile.
 *
 * @param {templatesSwitchToMobileParams} params - The parameters for this event
 * @property {number} page_index - the category index of the template
 * @property {Url} originappurl - only html template viewer send this property
 * @property {number} siteType - site_type
 * @property {string} category - the create page category
 * @property {Url} http_referrer - http_referrer
 * @property {string} platform - platform
 * @property {GUID} openingSiteID - template_id
 * @property {string} product - create page product filter
 * @return {LogObject<templatesSwitchToMobileParams>} LogObject<templatesSwitchToMobileParams>
 */
export function templatesSwitchToMobile(params) {
    return { evid: 27, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesSwitchToDesktop' event (evid: 28)
 *
 * Event description: templates -> switch to desktop.
 *
 * @param {templatesSwitchToDesktopParams} params - The parameters for this event
 * @property {number} page_index - the category index of the template
 * @property {Url} originappurl - only html template viewer send this property
 * @property {number} siteType - site_type
 * @property {string} category - the create page category
 * @property {Url} http_referrer - http_referrer
 * @property {string} platform - platform
 * @property {GUID} openingSiteID - template_id
 * @property {string} product - create page product filter
 * @return {LogObject<templatesSwitchToDesktopParams>} LogObject<templatesSwitchToDesktopParams>
 */
export function templatesSwitchToDesktop(params) {
    return { evid: 28, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'flashLoginSignupFormIsOpenedTest' event (evid: 3)
 *
 * Event description: (flash login) signup form is opened.
 *
 * @param {flashLoginSignupFormIsOpenedTestParams} params - The parameters for this event
 * @property {string} template - delete me
 * @property {string} template_name - delete me
 * @property {number} page_index - page_index
 * @property {GUID} siteid
 * @property {string} origin - test
 * @property {string} platform - platform
 * @property {GUID} msid - test
 * @property {string} eventData - test
 * @property {string} product - product
 * @return {LogObject<flashLoginSignupFormIsOpenedTestParams>} LogObject<flashLoginSignupFormIsOpenedTestParams>
 */
export function flashLoginSignupFormIsOpenedTest(params) {
    return { evid: 3, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'postRegPageClickOnGo' event (evid: 30)
 *
 * Event description: Top Funnel - Answer Category.
 *
 * @param {postRegPageClickOnGoParams} params - The parameters for this event
 * @property {string} structureId - structure_id
 * @property {string} location - Top, Bottom, Left etc' - Used in case of multiple buttons with the same name
 * @property {string} screen_version - screen version
 * @property {string} screenName - screen name
 * @property {number} page_index - page_index
 * @property {string} industryId - industry_id
 * @property {string} businessType - business_type
 * @property {boolean} self_declared - true= self declared by the user, false=inferred
 * @property {string} segment - segment
 * @property {string} main_industry - main_industry
 * @property {string} subcat - subcat
 * @property {string} businessSegment - business segment according to the new management  segments
 * @property {string} business_term_id - business_term_id
 * @property {string} flow - flow
 * @property {string} subIndustry - sub_industry
 * @property {string} sourceScreen - new/vertical or new/intro or second/intro
 * @property {string} target_flow - The target flow for this user in the intro
 * @property {string} platform - platform
 * @property {string} cat - category
 * @property {string} action - action
 * @property {string} product - product
 * @property {string} free_text - free_text
 * @return {LogObject<postRegPageClickOnGoParams>} LogObject<postRegPageClickOnGoParams>
 */
export function postRegPageClickOnGo(params) {
    return { evid: 30, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesSeeAllTemplates' event (evid: 46)
 *
 * Event description: When a user clicks on "see all templates" from the viewer
 * https://jira.wixpress.com/browse/MRND-2219
 *
 * @param {templatesSeeAllTemplatesParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {Url} originappurl - only html template viewer send this property
 * @property {string} to_category - templates page target category
 * @property {string} from_category - templates page origin category
 * @property {string} platform - platform
 * @property {string} template_id - template guid
 * @property {string} product - product
 * @return {LogObject<templatesSeeAllTemplatesParams>} LogObject<templatesSeeAllTemplatesParams>
 */
export function templatesSeeAllTemplates(params) {
    return { evid: 46, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesSawAnElement' event (evid: 47)
 *
 * Event description: templates -> saw an element
 *
 * @param {templatesSawAnElementParams} params - The parameters for this event
 * @property {string} wixsession - authentication token. if the session exists, then the user is logged in.
 * @property {string} sub_category - Sub category clicked from
 * @property {string} element_identifier - category name for categories and template_guid for templates
 * @property {Url} openingBrowserUrl - opening_browser_url
 * @property {string} template_type - ADI template or Editor template
 * @property {number} page_index - page_index
 * @property {Url} originappurl - only html template viewer send this property
 * @property {string} book - book name
 * @property {boolean} videoThumbnail - template shows a video when hovering on the thumbnail (TRUE/FALSE)
 * @property {string} element_name - element_name
 * @property {string} header_type - the type of header (default/sticky) in which the click was performed
 * @property {number} gallerydocsperpage - gallerydocsperpage
 * @property {GUID} tpsi - Templates Page Session ID
 * @property {number} galleryDocIndex - gallery_doc_index
 * @property {number} siteType - 1=flash, 2=html, 3=mobile, 4=facebook html, 5 = facebook flash, 6=blog(old)
 * @property {string} screen_type - the type of page in which the action was performed: category / search_results / main / home
 * @property {string} category - the create page category
 * @property {string} platform - platform
 * @property {GUID} template_id - in case of click on template thumbnail
 * @property {string} product - product
 * @return {LogObject<templatesSawAnElementParams>} LogObject<templatesSawAnElementParams>
 */
export function templatesSawAnElement(params) {
    return { evid: 47, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesClickOnElement' event (evid: 48)
 *
 * Event description: templates -> click on element
 *
 * @param {templatesClickOnElementParams} params - The parameters for this event
 * @property {string} wixsession - authentication token. if the session exists, then the user is logged in.
 * @property {string} sub_category - Sub category clicked from
 * @property {Url} openingBrowserUrl - opening_browser_url
 * @property {string} template_type - ADI template or Editor template
 * @property {number} page_index - page_index
 * @property {Url} originappurl - only html template viewer send this property
 * @property {string} book - book name
 * @property {boolean} videoThumbnail - template shows a video when hovering on the thumbnail (TRUE/FALSE)
 * @property {string} clicked_element_identifier - category name for categories and template_guid for templates
 * @property {string} header_type - the type of header (default/sticky) in which the click was performed
 * @property {string} clicked_element - the element that was clicked
 * @property {number} gallerydocsperpage - gallerydocsperpage
 * @property {GUID} tpsi - Templates Page Session ID
 * @property {number} galleryDocIndex - gallery_doc_index
 * @property {number} siteType - 1=flash, 2=html, 3=mobile, 4=facebook html, 5 = facebook flash, 6=blog(old)
 * @property {string} screen_type - the type of page in which the action was performed: category / search_results / main / home
 * @property {string} category - the create page category
 * @property {string} platform - platform
 * @property {GUID} template_id - in case of click on template thumbnail
 * @property {string} product - product
 * @property {string} micro_category - micro_category
 * @return {LogObject<templatesClickOnElementParams>} LogObject<templatesClickOnElementParams>
 */
export function templatesClickOnElement(params) {
    return { evid: 48, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesFullLoadingTimes' event (evid: 49)
 *
 * Event description: full loading times for templates page
 *
 * @param {templatesFullLoadingTimesParams} params - The parameters for this event
 * @property {number} dom_content_loaded_event_end - dom_content_loaded_event_end
 * @property {number} domain_lookup_end - domain_lookup_end
 * @property {number} page_index - page_index
 * @property {number} redirect_end - redirect_end
 * @property {number} domLoading_ts - dom_loading_ts
 * @property {number} connect_end - connect_end
 * @property {number} connect_start - connect_start
 * @property {number} domComplete_ts - domComplete_ts
 * @property {number} response_start - response_start
 * @property {number} dom_content_loaded_event_start - dom_content_loaded_event_start
 * @property {number} domain_lookup_start - domain_lookup_start
 * @property {number} request_start - request_start
 * @property {GUID} tpsi - Templates Page Session ID
 * @property {number} dom_interactive - dom_interactive
 * @property {number} screen_width - Width of client device screen
 * @property {number} fetch_start - fetch_start
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {number} navigation_start - navigation_start
 * @property {number} unload_event_end - unload_event_end
 * @property {number} window_width - Initial width of client window
 * @property {number} redirect_start - redirect_start
 * @property {number} unload_event_start - unload_event_start
 * @property {number} load_event_start - load_event_start
 * @property {number} screen_height - Height of client device screen
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {number} secure_connection_start - secure_connection_start
 * @property {number} avail_height - Returns the amount of vertical space in pixels available to the window
 * @property {number} load_event_end - load_event_end
 * @property {number} basic_loading_time - basic_loading_time
 * @property {number} response_end - response_end
 * @property {string} product - product
 * @return {LogObject<templatesFullLoadingTimesParams>} LogObject<templatesFullLoadingTimesParams>
 */
export function templatesFullLoadingTimes(params) {
    return { evid: 49, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesStartNowAdiBuilder' event (evid: 50)
 *
 * Event description: Clicking the "Start Now" ADI builder from no results search
 *
 * @param {templatesStartNowAdiBuilderParams} params - The parameters for this event
 * @property {string} wixsession - authentication token. if the session exists, then the user is logged in.
 * @property {Url} openingBrowserUrl - openingBrowserUrl
 * @property {string} template_type - ADI template or Editor template
 * @property {number} page_index - page_index
 * @property {string} criteria - Criteria searched
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {number} window_width - Initial width of client window
 * @property {number} total_suggested_results - total search results from external search suggestion
 * @property {number} screen_height - Height of client device screen
 * @property {number} window_height - Initial height of client window
 * @property {GUID} esi - ESI guid
 * @property {string} platform - platform
 * @property {number} avail_height - Returns the amount of vertical space in pixels available to the window
 * @property {string} product - product
 * @return {LogObject<templatesStartNowAdiBuilderParams>} LogObject<templatesStartNowAdiBuilderParams>
 */
export function templatesStartNowAdiBuilder(params) {
    return { evid: 50, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesPagePageFullyRendered' event (evid: 51)
 *
 * Event description: Templates page interaction is fully rendered and interactive to user
 *
 * @param {templatesPagePageFullyRenderedParams} params - The parameters for this event
 * @property {string} referralInfo - -
 * @property {string} interaction_type - Type of templates page interaction
 * @property {string} sub_category - List of subcategory filters
 * @property {string} event_data - data about templates loaded
 * @property {number} page_index - page_index
 * @property {string} book - book
 * @property {string} features - list of selected features
 * @property {number} gallerydocsperpage - Number of thumbnails on page
 * @property {string} styles - list of selected styles
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {string} colors - list of selected colors
 * @property {string} sorting - selected sorting
 * @property {number} loadingTime - Time to full rendering (interactive)
 * @property {string} origin - origin page
 * @property {string} category - the create page category
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<templatesPagePageFullyRenderedParams>} LogObject<templatesPagePageFullyRenderedParams>
 */
export function templatesPagePageFullyRendered(params) {
    return { evid: 51, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesTemplateAboveTheFoldImpression' event (evid: 52)
 *
 * Event description: When a user has a template above the fold on the Templates Page or clicks any component on the thumbnail
 *
 * @param {templatesTemplateAboveTheFoldImpressionParams} params - The parameters for this event
 * @property {number} page_view_index - Page Index
 * @property {string} sub_category - List of subcategory filters
 * @property {number} galleryDocsPerPage - gallery doc page
 * @property {number} page_index - page_index
 * @property {string} book - book
 * @property {string} features - list of selected features
 * @property {string} styles - list of selected styles
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {string} colors - list of selected colors
 * @property {number} window_width - Initial width of client window
 * @property {string} sorting - selected sorting
 * @property {number} galleryDocIndex - gallery doc index
 * @property {number} siteType - site type
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - category
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {number} avail_height - Returns the amount of horizontal space in pixels vertical to the window
 * @property {GUID} template_id - template guid
 * @property {string} product - product
 * @property {boolean} is_recommended - shown in recommended section
 * @property {string} template_description - template description
 * @property {string} badgeDescription - badge description
 * @return {LogObject<templatesTemplateAboveTheFoldImpressionParams>} LogObject<templatesTemplateAboveTheFoldImpressionParams>
 */
export function templatesTemplateAboveTheFoldImpression(params) {
    return { evid: 52, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesPagePerformanceTimingClient' event (evid: 53)
 *
 * Event description: Performance timing of segments
 *
 * @param {templatesPagePerformanceTimingClientParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {string} segment_name - Segment name
 * @property {string} context_param - (Optional) Additional info related to segment
 * @property {number} time - Time taken to execute segment
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<templatesPagePerformanceTimingClientParams>} LogObject<templatesPagePerformanceTimingClientParams>
 */
export function templatesPagePerformanceTimingClient(params) {
    return { evid: 53, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesPagination' event (evid: 54)
 *
 * Event description: User browsing through paginations
 *
 * @param {templatesPaginationParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {GUID} tpsi - Templates Page Session ID
 * @property {string} pagination_type - Page Click/Load More Button/Infinite Scroll
 * @property {number} page_clicked_index - Next page's index chosen by user
 * @property {string} category - Templates Page Category
 * @property {string} platform - platform
 * @property {string} product - product
 * @return {LogObject<templatesPaginationParams>} LogObject<templatesPaginationParams>
 */
export function templatesPagination(params) {
    return { evid: 54, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesPageMainCategoryMenu' event (evid: 55)
 *
 * Event description: Usage of the main category menu
 *
 * @param {templatesPageMainCategoryMenuParams} params - The parameters for this event
 * @property {string} sub_category - Current sub-category performed in
 * @property {string} click_source - header/tags/mobile_menu
 * @property {number} page_index - page_index
 * @property {string} book - CMS book name
 * @property {string} header_type - the type of header (default/sticky) in which the click/hover was performed
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} category_group - Category group (Business & Org, Store, etc.)
 * @property {string} selected_value - (Optional) chosen category
 * @property {string} category - Current main category performed in
 * @property {string} selected_sub_category - (Optional) chosen sub-category
 * @property {string} platform - platform
 * @property {string} action - Open/Close/Select
 * @property {string} product - product
 * @return {LogObject<templatesPageMainCategoryMenuParams>} LogObject<templatesPageMainCategoryMenuParams>
 */
export function templatesPageMainCategoryMenu(params) {
    return { evid: 55, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesPageSubCategoryFilter' event (evid: 56)
 *
 * Event description: Usage of the sub-category filter
 *
 * @param {templatesPageSubCategoryFilterParams} params - The parameters for this event
 * @property {string} previous_value - Previous list before the update
 * @property {number} page_index - page_index
 * @property {string} book - CMS book name
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} selected_value - (Optional) Subcategory clicked on
 * @property {string} category - Current main category performed in
 * @property {string} platform - platform
 * @property {string} action - add/remove/clear all
 * @property {string} product - product
 * @return {LogObject<templatesPageSubCategoryFilterParams>} LogObject<templatesPageSubCategoryFilterParams>
 */
export function templatesPageSubCategoryFilter(params) {
    return { evid: 56, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesPageTemplatesFilterPane' event (evid: 57)
 *
 * Event description: Usage of the templates filter pane
 *
 * @param {templatesPageTemplatesFilterPaneParams} params - The parameters for this event
 * @property {string} previous_value - Previous list before the update
 * @property {number} page_index - page_index
 * @property {string} book - CMS book name
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} selected_value - (Optional) feature/color/layout/style checked or cleared list
 * @property {string} category - Current main category performed in
 * @property {string} platform - platform
 * @property {string} filter_type - feature/color/layout/style (style relevant only for select action)
 * @property {string} action - open/close/hover/select/clear all/show filters/hide filters
 * @property {string} product - product
 * @return {LogObject<templatesPageTemplatesFilterPaneParams>} LogObject<templatesPageTemplatesFilterPaneParams>
 */
export function templatesPageTemplatesFilterPane(params) {
    return { evid: 57, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesWidgetAboveTheFoldImpression' event (evid: 61)
 *
 * Event description: When a user has the Templates Page widget above the fold
 *
 * @param {templatesWidgetAboveTheFoldImpressionParams} params - The parameters for this event
 * @property {string} sub_category - List of subcategory filters
 * @property {number} page_index - page_index
 * @property {string} widget_name - The vertical presented in the Widget (Blog, Online Store, etc.)
 * @property {string} book - book
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {number} window_width - Initial width of client window
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - Main category name
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {number} avail_height - Returns the amount of horizontal space in pixels vertical to the window
 * @property {string} product - product
 * @return {LogObject<templatesWidgetAboveTheFoldImpressionParams>} LogObject<templatesWidgetAboveTheFoldImpressionParams>
 */
export function templatesWidgetAboveTheFoldImpression(params) {
    return { evid: 61, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesWidgetActions' event (evid: 62)
 *
 * Event description: User engagement with the Templates Page widget
 *
 * @param {templatesWidgetActionsParams} params - The parameters for this event
 * @property {string} sub_category - List of subcategory filters
 * @property {number} tool_index - (optional)  Tool index number (1..4)
 * @property {number} page_index - page_index
 * @property {string} widget_name - The vertical presented in the Widget (Blog, Online Store, etc.)
 * @property {string} book - book
 * @property {string} clicked_element - Clicked Element: Thumbnail, Title, etc.
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {number} window_width - Initial width of client window
 * @property {number} tool_index_selected - (optional) Tool number to be presented
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - Main category name
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {number} avail_height - Returns the amount of horizontal space in pixels vertical to the window
 * @property {string} product - product
 * @return {LogObject<templatesWidgetActionsParams>} LogObject<templatesWidgetActionsParams>
 */
export function templatesWidgetActions(params) {
    return { evid: 62, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesVideoPreviewOpen' event (evid: 65)
 *
 * Event description: user clicked on a thumbnail with a video which opened the preview pop up
 *
 * @param {templatesVideoPreviewOpenParams} params - The parameters for this event
 * @property {boolean} is_search_result - Is result from suggestion ("like you")
 * @property {number} search_rank - External ("like you") suggestion ranking
 * @property {string} sub_category - List of subcategory filters
 * @property {number} total_search_results - total search results from external ("like you") search suggestion
 * @property {number} page_index - page_index
 * @property {string} book - book
 * @property {string} features - list of selected features
 * @property {number} gallerydocsperpage - gallery docs per page
 * @property {string} styles - list of selected styles
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {string} colors - list of selected colors
 * @property {GUID} search_guid - (optional) If from search results, GUID of matching search
 * @property {number} window_width - Initial width of client window
 * @property {string} sorting - selected sorting
 * @property {string} screen_type - the type of page in which the action was performed: category / search_results / main / home
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - the create page category
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {GUID} openingSiteID - template_id
 * @property {number} avail_height - Returns the amount of vertical space in pixels available to the window
 * @property {string} product - product
 * @return {LogObject<templatesVideoPreviewOpenParams>} LogObject<templatesVideoPreviewOpenParams>
 */
export function templatesVideoPreviewOpen(params) {
    return { evid: 65, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesCloseVideoPreview' event (evid: 69)
 *
 * Event description: user clicked on 'x' to close the video preview pop up or just moved out of the thumbnail
 *
 * @param {templatesCloseVideoPreviewParams} params - The parameters for this event
 * @property {boolean} is_search_result - Is result from suggestion ("like you")
 * @property {number} search_rank - External ("like you") suggestion ranking
 * @property {string} sub_category - List of subcategory filters
 * @property {number} total_search_results - total search results from external ("like you") search suggestion
 * @property {number} page_index - page_index
 * @property {string} book - book
 * @property {string} features - list of selected features
 * @property {number} gallerydocsperpage - gallery docs per page
 * @property {string} styles - list of selected styles
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {number} video_full_duration - video_full_duration
 * @property {string} colors - list of selected colors
 * @property {GUID} search_guid - (optional) If from search results, GUID of matching search
 * @property {number} window_width - Initial width of client window
 * @property {string} sorting - selected sorting
 * @property {DateTime} video_end_time - video end time
 * @property {string} screen_type - the type of page in which the action was performed: category / search_results / main / home
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - the create page category
 * @property {string} video_status - 'have_nothing', 'have_metadata', 'have_current_data', 'have_future_data', 'have_enough_data'
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {DateTime} video_start_time - video start time
 * @property {number} video_playback_position - video_playback_position
 * @property {GUID} openingSiteID - template_id
 * @property {number} avail_height - Returns the amount of vertical space in pixels available to the window
 * @property {string} product - product
 * @return {LogObject<templatesCloseVideoPreviewParams>} LogObject<templatesCloseVideoPreviewParams>
 */
export function templatesCloseVideoPreview(params) {
    return { evid: 69, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesGenericPageView' event (evid: 78)
 *
 * Event description: page load - templates page domain
 *
 * @param {templatesGenericPageViewParams} params - The parameters for this event
 * @property {boolean} is_search_result - Is result from suggestion ("like you")
 * @property {string} wixsession - authentication token. if the session exists, then the user is logged in.
 * @property {number} search_rank - External ("like you") suggestion ranking
 * @property {string} sub_category - List of subcategory filters
 * @property {number} total_search_results - total search results from external ("like you") search suggestion
 * @property {number} page_index - page_index
 * @property {string} criteria - search term used in search
 * @property {string} features - list of selected features
 * @property {number} gallerydocsperpage - number of templates presented on the page
 * @property {string} styles - list of selected styles
 * @property {string} previous_url - previous URL (referrer)
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} layouts - list of selected layouts
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {string} colors - list of selected colors
 * @property {GUID} search_guid - If from search results, GUID of matching search
 * @property {number} window_width - Initial width of client window
 * @property {string} sorting - selected sorting
 * @property {string} origin - intro / new site / organic
 * @property {string} screen_type - category / search_results / main / home
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - the create page category
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {string} eventData - json with template list: returns the number of templates on the page that was loaded
 * @property {number} avail_height - Returns the amount of horizontal space in pixels vertical to the window
 * @property {number} page_number - page number
 * @property {string} product - product
 * @return {LogObject<templatesGenericPageViewParams>} LogObject<templatesGenericPageViewParams>
 */
export function templatesGenericPageView(params) {
    return { evid: 78, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesWidgetLoaded' event (evid: 79)
 *
 * Event description: Widget was loaded (not necessarily viewed)
 *
 * @param {templatesWidgetLoadedParams} params - The parameters for this event
 * @property {string} sub_category - List of subcategory filters
 * @property {number} page_index - page_index
 * @property {string} widget_name - The vertical presented in the Widget (Blog, Online Store, etc.)
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {number} window_width - Initial width of client window
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - Main category name
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {number} avail_height - Returns the amount of horizontal space in pixels vertical to the window
 * @property {string} product - product
 * @return {LogObject<templatesWidgetLoadedParams>} LogObject<templatesWidgetLoadedParams>
 */
export function templatesWidgetLoaded(params) {
    return { evid: 79, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesHoverOnWidget' event (evid: 80)
 *
 * Event description: Hover on the widget
 *
 * @param {templatesHoverOnWidgetParams} params - The parameters for this event
 * @property {string} sub_category - List of subcategory filters
 * @property {number} page_index - page_index
 * @property {string} widget_name - The vertical presented in the Widget (Blog, Online Store, etc.)
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {number} screen_width - Width of client device screen
 * @property {number} avail_width - Returns the amount of horizontal space in pixels available to the window
 * @property {number} hover_ms - hover duration in milliseconds
 * @property {number} window_width - Initial width of client window
 * @property {number} screen_height - Height of client device screen
 * @property {string} category - Main category name
 * @property {number} window_height - Initial height of client window
 * @property {string} platform - platform
 * @property {number} avail_height - Returns the amount of horizontal space in pixels vertical to the window
 * @property {string} product - product
 * @return {LogObject<templatesHoverOnWidgetParams>} LogObject<templatesHoverOnWidgetParams>
 */
export function templatesHoverOnWidget(params) {
    return { evid: 80, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'copyOfTemplatesSwitchToTablet' event (evid: 98)
 *
 * Event description: user switched to tablet view
 *
 * @param {copyOfTemplatesSwitchToTabletParams} params - The parameters for this event
 * @property {number} page_index - page_index
 * @property {Url} originappurl - only html template viewer send this property
 * @property {number} siteType - site type
 * @property {string} category - the create page category
 * @property {string} platform - platform
 * @property {GUID} openingSiteID - template id
 * @property {string} product - product
 * @return {LogObject<copyOfTemplatesSwitchToTabletParams>} LogObject<copyOfTemplatesSwitchToTabletParams>
 */
export function copyOfTemplatesSwitchToTablet(params) {
    return { evid: 98, src: 2, endpoint: 'spettatore_bi', params: params };
}
/**
 * A function to create a log object for the 'templatesPageGalleryFilterPane' event (evid: 99)
 *
 * Event description: Usage of the templates page filter pane
 *
 * @param {templatesPageGalleryFilterPaneParams} params - The parameters for this event
 * @property {string} updated_layouts - List of selected layouts after the update
 * @property {number} page_index - page_index
 * @property {string} updated_sorting - Applied sorting after the update
 * @property {string} book - CMS book name
 * @property {string} updated_features - List of selected features after the update
 * @property {GUID} tpsi - Templates Page Session ID (sticky for session, not refresh)
 * @property {string} updated_colors - List of selected colors after the update
 * @property {string} subcategory - Current sub-category performed in
 * @property {string} updated_styles - List of selected styles after the update
 * @property {string} menu_name - features / colors / layouts / styles / sort / all
 * @property {string} category - Current main category performed in
 * @property {string} clicked_value - the filter/sort value on which the user clicked (either to select or unselect)
 * @property {string} platform - platform
 * @property {string} previous_values - Previous list of values before the update
 * @property {string} action - open / close / select / unselect / hover / clear / show / hide
 * @property {string} updated_values - List of selected values after the update
 * @property {string} product - product
 * @return {LogObject<templatesPageGalleryFilterPaneParams>} LogObject<templatesPageGalleryFilterPaneParams>
 */
export function templatesPageGalleryFilterPane(params) {
    return { evid: 99, src: 2, endpoint: 'spettatore_bi', params: params };
}
//# sourceMappingURL=index.js.map